import React, { useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/id'
import {
  Button,
  Descriptions,
  Drawer,
  Icons,
  Space,
  Typography,
} from '@pankod/refine-antd'
import { useOne, useCustomMutation, useNotification } from '@pankod/refine-core'

import { TCertificateTTEGetSubmissionResponse } from '../types'
import {
  formatCapabilityLabel,
  formatNumberValue,
  formatTextValue,
  renderCertificateTypeTag,
} from '../display'
import { CertificatePreview } from './CertificatePreview'
import { ApprovalWorkflow } from './ApprovalWorkflow'
import {
  ApproveModal,
  RejectModal,
  SignModal,
  TSignFormValues,
} from './ActionModals'
import { TCommonResponse } from 'src/interfaces/common'
import { ParticipantsModal } from './ParticipantsModal'
import { PengajuanStatusTag } from '@resources/tanda-tangan-elektronik/penandatanganan-sertifikat/components/PengajuanStatusTag'

dayjs.extend(utc)
dayjs.extend(timezone)

const { Text } = Typography

type TSignSubmissionError = {
  response?: {
    data?: {
      error?: {
        details?: { message?: string }[]
        message?: string
      }
    }
  }
  message?: string
}

const getSignErrorMessage = (error: unknown): string => {
  if (typeof error !== 'object' || error === null) {
    return 'Terjadi kesalahan saat menandatangani sertifikat'
  }

  const submissionError = error as TSignSubmissionError

  return (
    submissionError.response?.data?.error?.details?.[0]?.message ||
    submissionError.response?.data?.error?.message ||
    submissionError.message ||
    'Terjadi kesalahan saat menandatangani sertifikat'
  )
}

interface TSubmissionDetailDrawerProps {
  open: boolean
  submissionId: string | null
  onClose: () => void
  onSuccessRefetch: () => Promise<unknown>
}

export const SubmissionDetailDrawer: React.FC<TSubmissionDetailDrawerProps> = ({
  open,
  submissionId,
  onClose,
  onSuccessRefetch,
}) => {
  const { open: openNotification } = useNotification()

  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [signModalOpen, setSignModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [showParticipantsModal, setShowParticipantsModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const detailQueryResult = useOne<
    TCommonResponse<TCertificateTTEGetSubmissionResponse>
  >({
    resource: 'certificate-tte-submissions',
    id: submissionId as string,
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!submissionId,
    },
  })

  const { mutateAsync: mutateCustom } = useCustomMutation()
  const pengajuan = detailQueryResult?.data?.data?.data

  const refreshSubmission = async () => {
    await Promise.allSettled([
      Promise.resolve().then(() => detailQueryResult.refetch()),
      Promise.resolve().then(onSuccessRefetch),
    ])
  }

  const handleApproveConfirm = async () => {
    if (!submissionId) return
    setIsSubmitting(true)

    try {
      await mutateCustom({
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/certificate-tte-submissions/${submissionId}/approve`,
        method: 'post',
        values: {},
        dataProviderName: 'lms',
        successNotification: false,
      })
      await refreshSubmission()
      openNotification?.({
        type: 'success',
        message:
          'Proses penandatanganan elektronik sedang berlangsung, harap cek secara berkala untuk mengetahui progres penandatanganan sertifikat ini.',
        description: 'Sukses',
      })
      setApproveModalOpen(false)
    } catch {
      // The data provider displays the mutation error notification.
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSignConfirm = async (
    values: TSignFormValues,
    setFieldError: (name: string, error: string) => void,
  ) => {
    if (!submissionId) return
    setIsSubmitting(true)

    try {
      await mutateCustom({
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/certificate-tte-submissions/${submissionId}/sign`,
        method: 'post',
        values: {
          nik: values['bsre-nik'],
          passphrase: values['bsre-phrase'],
        },
        dataProviderName: 'lms',
        errorNotification: false,
      })
      await refreshSubmission()
      openNotification?.({
        type: 'success',
        message: 'Berhasil menandatangani sertifikat.',
      })
      setSignModalOpen(false)
    } catch (error: unknown) {
      const errorMessage = getSignErrorMessage(error)

      openNotification?.({
        type: 'error',
        message: errorMessage,
        description: 'Gagal Menandatangani',
      })

      if (errorMessage.toLowerCase().includes('nik')) {
        setFieldError('bsre-nik', errorMessage)
      } else if (errorMessage.toLowerCase().includes('passphrase')) {
        setFieldError('bsre-phrase', errorMessage)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRejectConfirm = async () => {
    if (!submissionId) return
    setIsSubmitting(true)

    try {
      await mutateCustom({
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/certificate-tte-submissions/${submissionId}/reject`,
        method: 'post',
        values: { alasan: rejectReason },
        dataProviderName: 'lms',
      })
      await refreshSubmission()
      openNotification?.({
        type: 'success',
        message: 'Berhasil menolak pengajuan sertifikat.',
      })
      setRejectModalOpen(false)
      setRejectReason('')
    } catch {
      // Keep the modal and rejection reason available for retry.
    } finally {
      setIsSubmitting(false)
    }
  }

  const isComplete = pengajuan?.status === 'selesai'
  const isRejected = pengajuan?.status === 'ditolak'
  const isTtdUlang = pengajuan?.status === 'tte_ulang'
  const isSiapDiproses = pengajuan?.status === 'siap_diproses'
  const currentWorkflow =
    pengajuan?.workflows?.[pengajuan?.currentWorkflowIndex]
  const isMyTurn =
    isSiapDiproses || (isTtdUlang && currentWorkflow?.role === 'signer')

  const drawerFooter = pengajuan ? (
    <div>
      {isMyTurn && (
        <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
          {currentWorkflow?.role === 'approver' && !isTtdUlang && (
            <Button
              type="primary"
              icon={<Icons.CheckOutlined />}
              onClick={() => setApproveModalOpen(true)}
            >
              Setujui
            </Button>
          )}
          {currentWorkflow?.role === 'signer' && (
            <Button
              type="primary"
              icon={<Icons.EditOutlined />}
              onClick={() => setSignModalOpen(true)}
            >
              Tandatangani
            </Button>
          )}
          {!isTtdUlang && (
            <Button
              danger
              icon={<Icons.CloseOutlined />}
              onClick={() => setRejectModalOpen(true)}
            >
              Tolak
            </Button>
          )}
        </Space>
      )}
      {!isMyTurn && (
        <>
          {isComplete && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              <Icons.CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Typography.Text style={{ color: '#52c41a', fontSize: 13 }}>
                Semua pejabat telah menandatangani sertifikat ini
              </Typography.Text>
            </div>
          )}
          {isRejected && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              <Icons.CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              <Typography.Text style={{ color: '#ff4d4f', fontSize: 13 }}>
                Pengajuan ini telah ditolak
              </Typography.Text>
            </div>
          )}
          {!isComplete && !isRejected && !isTtdUlang && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              <Icons.ClockCircleOutlined style={{ color: '#8c8c8c' }} />
              <Typography.Text style={{ color: '#8c8c8c', fontSize: 13 }}>
                Bukan giliran Anda untuk memberikan persetujuan
              </Typography.Text>
            </div>
          )}
        </>
      )}
    </div>
  ) : null

  return (
    <>
      <Drawer
        open={open}
        title={pengajuan?.namaDiklat ?? 'Detail Pengajuan'}
        width={'70%'}
        onClose={onClose}
        destroyOnClose
        footer={drawerFooter}
      >
        {detailQueryResult?.isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : pengajuan ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Descriptions
              column={3}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
              layout="vertical"
              labelStyle={{ fontWeight: 600 }}
            >
              <Descriptions.Item label="Nama Diklat" span={3}>
                {formatTextValue(pengajuan.namaDiklat)}
              </Descriptions.Item>
              <Descriptions.Item label="Nama UPT">
                {formatTextValue(pengajuan.namaPenyelenggara)}
              </Descriptions.Item>
              <Descriptions.Item label="Tanggal Pengajuan">
                {pengajuan.tanggalPengajuan &&
                dayjs(pengajuan.tanggalPengajuan).isValid()
                  ? dayjs(pengajuan.tanggalPengajuan)
                      .tz('Asia/Jakarta')
                      .format('DD MMMM YYYY HH:mm [(GMT+7)]')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Jumlah Kelas">
                {formatNumberValue(pengajuan.jumlahKelas, 'Kelas')}
              </Descriptions.Item>
              <Descriptions.Item label="Jumlah Penerima">
                {typeof pengajuan.jumlahPeserta === 'number' ? (
                  <div className="flex items-center justify-between">
                    <span>
                      {formatNumberValue(pengajuan.jumlahPeserta, 'Orang')}
                    </span>
                    <Button
                      icon={<Icons.TeamOutlined />}
                      size="small"
                      type="ghost"
                      onClick={() => {
                        setShowParticipantsModal(true)
                      }}
                    >
                      Daftar Penerima
                    </Button>
                  </div>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <PengajuanStatusTag status={pengajuan.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Tipe Sertifikat">
                {pengajuan.kapabilitasSertifikat && pengajuan.tipeSertifikat ? (
                  <div className="flex justify-between gap-2">
                    <span>
                      {formatCapabilityLabel(pengajuan.kapabilitasSertifikat)}
                    </span>
                    {renderCertificateTypeTag(pengajuan.tipeSertifikat)}
                  </div>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Dokumen Pendukung" span={3}>
                {pengajuan.supportingDocumentUrl ? (
                  <Button
                    type="link"
                    icon={<Icons.FileTextOutlined />}
                    onClick={async () => {
                      const data = await detailQueryResult.refetch()
                      window.open(
                        data.data?.data.data.supportingDocumentUrl,
                        '_blank',
                      )
                    }}
                    style={{ padding: 0 }}
                  >
                    Lihat Dokumen
                  </Button>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
              <div style={{ flex: '0 0 50%', maxWidth: '50%' }}>
                <Text
                  strong
                  style={{ display: 'block', marginBottom: 8, fontSize: 13 }}
                >
                  Preview Sertifikat
                </Text>
                <CertificatePreview
                  pengajuan={pengajuan}
                  submissionId={submissionId as string}
                />
              </div>

              <div
                style={{
                  flex: '0 0 50%',
                  maxWidth: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <ApprovalWorkflow pengajuan={pengajuan} variant="signer" />
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>

      <ApproveModal
        open={approveModalOpen}
        namaDiklat={pengajuan?.namaDiklat}
        isSubmitting={isSubmitting}
        onClose={() => setApproveModalOpen(false)}
        onConfirm={handleApproveConfirm}
      />

      <SignModal
        open={signModalOpen}
        namaDiklat={pengajuan?.namaDiklat}
        isSubmitting={isSubmitting}
        onClose={() => setSignModalOpen(false)}
        onConfirm={handleSignConfirm}
      />

      <RejectModal
        open={rejectModalOpen}
        namaDiklat={pengajuan?.namaDiklat}
        isSubmitting={isSubmitting}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onClose={() => {
          setRejectModalOpen(false)
          setRejectReason('')
        }}
        onConfirm={handleRejectConfirm}
      />

      <ParticipantsModal
        open={showParticipantsModal}
        submissionId={pengajuan?.id || ''}
        onClose={() => setShowParticipantsModal(false)}
      />
    </>
  )
}
